/* ============================================================
   IMANUEL — mobile sticky add-to-cart (product pages)
   ------------------------------------------------------------
   The sticky bar is a remote control for the page's buy box
   (snippets/imanuel-bundle-inline.liquid):

   • every piece has a colour + size  → a tap ADDS TO THE BAG. It presses the
     buy box's own button, so bundle pricing, line-item properties, the
     double-add guard, the toast and the cart drawer all stay in one place.
   • something still needs picking     → a tap jumps to the first piece that
     is missing a colour/size, opens it, and says what is missing.

   State is read from the DOM (the main button's `disabled` flag and the active
   tier's data-price), so this file does not depend on imanuel-handoff.js
   internals.
   ============================================================ */
(function () {
  const bar = document.getElementById("sticky-atc");
  const btn = document.getElementById("sticky-atc-add");
  if (!bar || !btn) return;

  const unitEl = bar.querySelector("[data-sticky-unit]");
  const totalEl = bar.querySelector("[data-sticky-total]");
  const nowEl = bar.querySelector("[data-sticky-now]");
  const wasEl = bar.querySelector("[data-sticky-was]");
  const idleLabel = (btn.textContent || "").trim();

  let busy = false;
  let busyTimer = 0;
  let coolDown = false; // standard theme form only: fixed pause instead of a "done" signal
  let lastDone = 0; // when the last add settled — swallows the tail of a double tap

  const i18n = () => window.IMANUEL_I18N || {};
  /** Rejects empty values and Shopify's "translation missing: …" sentinel. */
  const str = (val) =>
    (typeof val === "string" && val && val.indexOf("translation missing") !== 0 ? val : "");

  /* Same rule as activeBundleHost() in imanuel-handoff.js: the page-level buy
     box, never the copy injected into the theme's quick-add dialog (the dialog
     re-uses the same ids). */
  function buyBox() {
    const all = [...document.querySelectorAll("[data-bundle-inline]:not(script)")]
      .filter((el) => !el.closest("dialog.quick-add-modal"));
    return all.length ? all[all.length - 1] : null;
  }

  /** The button that really adds to the bag. */
  function mainButton() {
    const host = buyBox();
    if (host) return host.querySelector("#bundle-add");
    // Products that keep the theme's standard form (e.g. gift cards).
    const std = document.querySelector(
      "#MainContent .buy-buttons-block button[name='add'], #MainContent .buy-buttons-block button[type='submit']"
    );
    return std && !std.closest("dialog") ? std : null;
  }

  function mainLabel(main) {
    if (!main) return "";
    const el = main.querySelector(".bundle-add-btn__label") || main;
    return (el.textContent || "").replace(/\s+/g, " ").trim();
  }

  /* Every write below is "only if it changed": an unchanged attribute write
     still queues a MutationObserver record, and sync() runs from one. */
  const setText = (el, v) => { if (el && el.textContent !== v) el.textContent = v; };
  const setHidden = (el, v) => { if (el && el.hidden !== v) el.hidden = v; };
  const setAttr = (el, k, v) => { if (el && el.getAttribute(k) !== v) el.setAttribute(k, v); };

  function sync() {
    const main = mainButton();

    // The buy box disables its button while the request is in flight and
    // re-enables it afterwards — that is our "done" signal. (coolDown covers
    // the standard theme form, whose button never disables.)
    if (busy && !coolDown && main && !main.disabled) release();

    const ready = !!main && !main.disabled;
    setAttr(bar, "data-state", busy ? "busy" : ready ? "ready" : "pending");
    setAttr(btn, "aria-busy", busy ? "true" : "false");
    setText(btn, busy ? (str(i18n().cart && i18n().cart.adding) || idleLabel) : idleLabel);

    // Price line: unit price until the selection is complete, then the exact
    // total the tap will add.
    const t = ready || busy ? totals(buyBox()) : null;
    const showTotal = !!t && !!totalEl && !!nowEl;
    if (showTotal) {
      setText(nowEl, t.now);
      setText(wasEl, t.was);
      setHidden(wasEl, !t.was);
    }
    setHidden(totalEl, !showTotal);
    setHidden(unitEl, showTotal);
  }

  /** USD dollars → the visitor's display currency (same formatter the buy box uses). */
  function fmt(n) {
    const fx = window.imanuelDisplayCurrency;
    const out = fx && typeof fx.formatUsdAmount === "function" ? fx.formatUsdAmount(n) : "";
    if (out) return out;
    return "$" + n.toLocaleString("en-US", Number.isInteger(n) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* Total + compare-at for the current selection, from the same numbers the buy
     box prices with: the active tier's data-price / data-compare (pack price in
     USD), times the quantity stepper on single-piece products. Computed here
     rather than copied from #bundle-price because the display-currency script
     can leave that node on a stale amount after a currency switch. */
  function totals(host) {
    if (!host) return null;
    const tier = host.querySelector("#bundle-tiers .tier.is-active");
    const price = tier ? Number(tier.dataset.price) : NaN;
    if (Number.isFinite(price) && price > 0) {
      const qtyEl = host.querySelector("#bundle-qty-num");
      const single = host.hasAttribute("data-bundle-simple");
      const n = single ? Math.max(1, parseInt((qtyEl && qtyEl.textContent) || "1", 10) || 1) : 1;
      const now = Math.round(price * n * 100) / 100;
      const compare = Math.round(Number(tier.dataset.compare) * n * 100) / 100;
      return { now: fmt(now), was: Number.isFinite(compare) && compare > now ? fmt(compare) : "" };
    }
    // Unknown markup: fall back to whatever the buy box shows.
    const priceEl = host.querySelector("#bundle-price");
    const text = priceEl ? (priceEl.textContent || "").trim() : "";
    return text ? { now: text, was: "" } : null;
  }

  function release() {
    busy = false;
    coolDown = false;
    lastDone = Date.now();
    clearTimeout(busyTimer);
  }

  /** What the shopper still has to do, in the storefront language. */
  function hintText(host, main) {
    const label = mainLabel(main);
    const b = i18n().bundle || {};
    const pickEvery = str(b.pick_every);
    if (host && pickEvery && label && label === str(b.select_color_size)) {
      const unit = ((host.querySelector(".bundle-piece__label") || {}).textContent || "")
        .replace(/\s*\d+\s*$/, "")
        .trim()
        .toLowerCase();
      return pickEvery.replace(/\{unit\}/g, unit || "piece");
    }
    return label; // SELECT SIZE · COMING SOON · UNAVAILABLE …
  }

  function flash(el) {
    el.classList.remove("buybox-flash");
    void el.offsetWidth; // restart the animation
    el.classList.add("buybox-flash");
  }

  function guideToSelection() {
    const host = buyBox();
    const box = host || document.querySelector("#MainContent .buy-buttons-block");
    if (!box) return;

    let target = box;
    let piece = null;
    if (host) {
      piece = host.querySelector(".bundle-piece:not(.is-complete)");
      if (piece) {
        // Open that piece's colour/size rows (row click → showBundleStage()).
        if (!piece.classList.contains("is-active")) {
          const head = piece.querySelector(".bundle-piece__head");
          if (head) head.click();
        }
        target = piece;
      }
    }

    const calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "center" });
    flash(target);

    // Nothing left to pick (an add is still in flight, or the buy box is
    // mid-render): showing the button's own label as a "hint" would be noise.
    if (host && !piece) return;

    // Screen-reader / keyboard users land on the control they need next.
    const next = piece && piece.querySelector("button:not([disabled])");
    if (next) next.focus({ preventScroll: true });

    const msg = hintText(host, mainButton());
    if (msg && typeof window.imanuelToast === "function") window.imanuelToast(msg);
  }

  btn.addEventListener("click", () => {
    if (busy || Date.now() - lastDone < 600) return;
    const main = mainButton();
    if (!main || main.disabled) {
      guideToSelection();
      return;
    }

    const bundle = !!buyBox();
    main.click();

    if (bundle && main.disabled) {
      // The buy box disables its button synchronously once the add starts and
      // re-enables it when the request settles; the timer is only a safety net.
      busy = true;
      clearTimeout(busyTimer);
      busyTimer = window.setTimeout(() => { release(); sync(); }, 30000);
    } else if (!bundle) {
      // Standard theme form: its button never disables, so hold a short pause
      // instead — otherwise a double tap would add twice.
      busy = true;
      coolDown = true;
      clearTimeout(busyTimer);
      busyTimer = window.setTimeout(() => { release(); sync(); }, 1500);
    }
    sync();
  });

  /* Keep the bar in step with the buy box: tier / colour / size changes, the
     add request starting and finishing, section re-renders, currency switch.
     Records that come from the bar itself are ignored and sync() only writes
     when a value changed, so this cannot feed itself even if the bar is ever
     rendered inside the observed root. */
  const root = document.getElementById("MainContent") || document.body;
  new MutationObserver((records) => {
    if (records.every((r) => bar.contains(r.target))) return;
    sync();
  }).observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["disabled", "hidden"],
  });
  // The currency script repaints the buy box right after this event fires.
  window.addEventListener("imanuel:display-currency", () => window.setTimeout(sync, 0));
  document.addEventListener("DOMContentLoaded", sync);
  window.addEventListener("pageshow", sync); // back/forward cache restores

  /* Toasts sit just above the bar instead of on top of its button. */
  const measure = () => {
    const h = bar.offsetHeight;
    if (h) document.documentElement.style.setProperty("--sticky-atc-h", h + "px");
  };
  measure();
  window.addEventListener("resize", measure);

  sync();
})();
